import { useState, useEffect, useCallback, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { Communicator } from '../../../tools/Communication';
import { LayoutStoreInstance } from '../../../LayoutStore';
import i18n from '@/utils/i18n';
import {
  IsSelectableClaim,
  ViewerContext,
  IsHumanResources,
} from '../../../providers/ViewerContextProvider';
import {
  IUserLocation,
  ISubscription,
  ITransaction,
  UserDetailsResponse,
} from '../types';
import { IUser } from '../userInterfaces';
import { ReducedStore } from '../UserDetailTab';
import { IdName } from '../../../components/EditMerchantOrder/HandleStatusChange';

interface UseUserDataReturn {
  // User data
  user: IUser | null;
  locations: IUserLocation[];
  subscription: ISubscription | null;
  oneTimeToken: string | null;
  phoneNumber: string | null;
  linkedStore: ReducedStore | null;
  firstCheckoutStore: ReducedStore | null;
  userClaim: string | null;
  linkedStoreContainerStations: IdName[] | null;

  // Transaction data
  transactionHistory: ITransaction[];
  transactionHistoryLoaded: boolean;
  transactionHistoryLoading: boolean;

  // Loading states
  hasErrorLoading: boolean;

  // Actions
  reloadUser: (userId: string) => void;
  loadTransactionHistory: (userId: string) => void;
  sendResetPasswordMail: () => void;
  sendTestNotification: () => void;
  copyJwtToClipboard: () => void;
  setUserDisabled: (disabled: boolean) => void;
  toggleIsInternal: () => void;
  toggleIsChargeableForOverdue: () => void;
  deleteUser: (sendDeletionEvent: boolean) => void;
  setCheckoutLimit: (
    isUnlimited: boolean,
    isDefault: boolean,
    limit: number,
  ) => void;
  setNewPassword: (password: string) => void;
  setNewPhoneNumber: (
    oldPhoneNumber: string,
    newPhoneNumber: string,
  ) => Promise<void>;
}

export const useUserData = (userId: string): UseUserDataReturn => {
  // Context and navigation
  const viewer = useContext(ViewerContext);
  const history = useHistory();

  // State
  const [user, setUser] = useState<IUser | null>(null);
  const [locations, setLocations] = useState<IUserLocation[]>([]);
  const [subscription, setSubscription] = useState<ISubscription | null>(null);
  const [oneTimeToken, setOneTimeToken] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [linkedStore, setLinkedStore] = useState<ReducedStore | null>(null);
  const [firstCheckoutStore, setFirstCheckoutStore] =
    useState<ReducedStore | null>(null);
  const [userClaim, setUserClaim] = useState<string | null>(null);
  const [linkedStoreContainerStations, setLinkedStoreContainerStations] =
    useState<IdName[] | null>(null);

  const [transactionHistory, setTransactionHistory] = useState<ITransaction[]>(
    [],
  );
  const [transactionHistoryLoaded, setTransactionHistoryLoaded] =
    useState(false);
  const [transactionHistoryLoading, setTransactionHistoryLoading] =
    useState(false);

  const [hasErrorLoading, setHasErrorLoading] = useState(false);

  // Load user data
  const reloadUser = useCallback((userId: string) => {
    setHasErrorLoading(false);
    setUser(null);

    Communicator.Get('/Users/GetUserDetailInfo', { userId })
      .then((response) => response.json())
      .then((response: UserDetailsResponse) => {
        setLocations(response.currentLocations);
        setUser(response.user);
        setSubscription(response.subscription);
        setOneTimeToken(response.oneTimeToken);
        setPhoneNumber(response.phoneNumber);
        setLinkedStore(response.linkedStore);
        setFirstCheckoutStore(response.firstCheckoutStore);
        setUserClaim(
          response.user.claims.find((c) => IsSelectableClaim(c)) || null,
        );
        setLinkedStoreContainerStations(response.storeContainerStations);
      })
      .catch(() => {
        setHasErrorLoading(true);
      });
  }, []);

  // Load transaction history
  const loadTransactionHistory = useCallback(
    (userId: string) => {
      if (transactionHistoryLoaded || transactionHistoryLoading) return;

      setTransactionHistoryLoading(true);
      Communicator.Get('/Users/GetUserTransactionHistory', { userId })
        .then((response) => response.json())
        .then((body) => {
          setTransactionHistory(body);
          setTransactionHistoryLoaded(true);
          setTransactionHistoryLoading(false);
        })
        .catch(() => {
          setTransactionHistoryLoading(false);
        });
    },
    [transactionHistoryLoaded, transactionHistoryLoading],
  );

  // Send reset password mail
  const sendResetPasswordMail = useCallback(() => {
    if (!user) return;

    Communicator.PostData('/Users/SendPasswordResetLink', { id: user.id })
      .then(async (data: Response) => {
        const response = await data.json();
        if (response.setPasswordLink) {
          window.prompt(
            'Copy this link and send it to the user so they can set a new password:',
            response.setPasswordLink,
          );
        }
      })
      .catch(() =>
        LayoutStoreInstance.setError(i18n.t('Users:errorSendingMail')),
      );
  }, [user]);

  // Send test notification
  const sendTestNotification = useCallback(() => {
    if (!user) return;

    LayoutStoreInstance.setSuccess(i18n.t('Users:sendingNotification'));

    Communicator.PostData('/Users/SendTestNotification', { userId: user.id })
      .then(() => {
        LayoutStoreInstance.setSuccess(i18n.t('Users:notificationSent'));
        setTimeout(() => reloadUser(userId), 1000);
      })
      .catch(() =>
        LayoutStoreInstance.setError(i18n.t('Users:errorSendingNotification')),
      );
  }, [user, userId, reloadUser]);

  // Copy JWT to clipboard
  const copyJwtToClipboard = useCallback(() => {
    if (!user) return;

    Communicator.Get('/Users/GetJwtTokenForUser', { userId: user.id })
      .then((response) => response.text())
      .then((jwt) => {
        navigator.clipboard.writeText(jwt);
        LayoutStoreInstance.setSuccess(i18n.t('Users:jwtCopied'));
      })
      .catch(() =>
        LayoutStoreInstance.setError(i18n.t('Users:errorCopyingJwt')),
      );
  }, [user]);

  // Set user disabled/enabled
  const setUserDisabled = useCallback(
    (disabled: boolean) => {
      if (!user) return;

      if (disabled) {
        LayoutStoreInstance.setSuccess(i18n.t('Users:deactivatingUser'));
      } else {
        LayoutStoreInstance.setSuccess(i18n.t('Users:activatingUser'));
      }

      Communicator.PostData(
        `/Users/SetAccountActivated?userId=${user.id}&disabled=${disabled}`,
        {},
      )
        .then(() => {
          LayoutStoreInstance.setSuccess(
            i18n.t('Users:userActiveStatusChanged'),
          );
          reloadUser(userId);
        })
        .catch(() => {
          LayoutStoreInstance.setError(
            i18n.t('Users:errorChangingUserActiveStatus'),
          );
        });
    },
    [user, userId, reloadUser],
  );

  // Toggle internal status
  const toggleIsInternal = useCallback(() => {
    if (!user) return;

    const newVal = !user.isInternal;
    Communicator.PostData(
      `/Users/SetAccountInternal?userId=${user.id}&isInternal=${newVal}`,
      {},
    )
      .then(() => {
        LayoutStoreInstance.setSuccess(
          i18n.t('Users:userInternalStatusChanged'),
        );
        reloadUser(userId);
      })
      .catch(() => {
        LayoutStoreInstance.setError(
          i18n.t('Users:errorChangingUserInternalStatus'),
        );
      });
  }, [user, userId, reloadUser]);

  // Toggle chargeable for overdue
  const toggleIsChargeableForOverdue = useCallback(() => {
    if (!user) return;

    const newVal = !user.isChargeableForOverdueContainers;
    Communicator.PostData(
      `/Users/SetChargableForOverdue?userId=${user.id}&chargeableForOverdue=${newVal}`,
      {},
    )
      .then(() => {
        LayoutStoreInstance.setSuccess(
          i18n.t('Users:isChargedForOverdueContainersChanged'),
        );
        reloadUser(userId);
      })
      .catch(() => {
        LayoutStoreInstance.setError(
          i18n.t('Users:errorChangingIsChargedForOverdueContainers'),
        );
      });
  }, [user, userId, reloadUser]);

  // Delete user
  const deleteUser = useCallback(
    (sendDeletionEvent: boolean) => {
      if (!user) return;

      Communicator.PostData('/Users/DeleteUser', {
        userId: user.id,
        sendDeletionEvent,
      })
        .then(() => {
          LayoutStoreInstance.setSuccess(i18n.t('Users:userDeleted'));

          // If viewer is human resources, redirect to users list instead of reloading
          if (IsHumanResources(viewer)) {
            history.push('/users');
          } else {
            reloadUser(userId);
          }
        })
        .catch(async (data: Response) => {
          const response = await data.json();
          if (response.errors && response.errors.length > 0) {
            LayoutStoreInstance.setError(
              'Error: ' + response.errors.join(', '),
            );
          } else {
            LayoutStoreInstance.setError(i18n.t('Users:unknownError'));
          }
        });
    },
    [user, userId, reloadUser, viewer, history],
  );

  // Set checkout limit
  const setCheckoutLimit = useCallback(
    (isUnlimited: boolean, isDefault: boolean, limit: number) => {
      if (!user) return;

      Communicator.PostData('/Users/SetCheckoutLimit', {
        userId: user.id,
        isUnlimited,
        isDefault,
        checkoutLimit: limit,
      })
        .then(() => {
          reloadUser(userId);
        })
        .catch(() => {
          alert(i18n.t('Users:unknownError'));
          reloadUser(userId);
        });
    },
    [user, userId, reloadUser],
  );

  // Set new password
  const setNewPassword = useCallback(
    (password: string) => {
      if (!user) return;

      if (
        !window.confirm(
          `Are you sure you want to change the password for user '${user.userName}'?`,
        )
      ) {
        return;
      }

      Communicator.PostData('/Users/SetUserPassword', {
        userId: user.id,
        password,
      })
        .then(() => {
          LayoutStoreInstance.setSuccess(
            i18n.t('Users:passwordChangeSuccessful'),
          );
          reloadUser(userId);
        })
        .catch((data: Response) => {
          data.json().then((errors: string[]) => {
            LayoutStoreInstance.setError(
              'Error changing password: ' + errors.join(' '),
            );
          });
        });
    },
    [user, userId, reloadUser],
  );

  // Set new phone number
  const setNewPhoneNumber = useCallback(
    async (oldPhoneNumber: string, newPhoneNumber: string) => {
      if (!user) return;

      LayoutStoreInstance.setSuccess(i18n.t('Users:changingPhoneNumber'));

      try {
        await Communicator.PostData('/Users/ChangeUserPhoneNumber', {
          userId: user.id,
          oldPhoneNumber: user.phoneNumber,
          newPhoneNumber,
        });

        LayoutStoreInstance.setSuccess(i18n.t('Users:phoneNumberChanged'));
        reloadUser(userId);
      } catch (data: any) {
        LayoutStoreInstance.setError(i18n.t('Users:errorChangingPhoneNumber'));
        const content = await data.text();
        throw new Error(content);
      }
    },
    [user, userId, reloadUser],
  );

  // Load user data on mount
  useEffect(() => {
    if (userId) {
      reloadUser(userId);
    }
  }, [userId, reloadUser]);

  return {
    // User data
    user,
    locations,
    subscription,
    oneTimeToken,
    phoneNumber,
    linkedStore,
    firstCheckoutStore,
    userClaim,
    linkedStoreContainerStations,

    // Transaction data
    transactionHistory,
    transactionHistoryLoaded,
    transactionHistoryLoading,

    // Loading states
    hasErrorLoading,

    // Actions
    reloadUser,
    loadTransactionHistory,
    sendResetPasswordMail,
    sendTestNotification,
    copyJwtToClipboard,
    setUserDisabled,
    toggleIsInternal,
    toggleIsChargeableForOverdue,
    deleteUser,
    setCheckoutLimit,
    setNewPassword,
    setNewPhoneNumber,
  };
};
